import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, DollarSign, Percent, Save, Sparkles, Droplets, Flame } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

export default function AdminSettings() {
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();

  const [settings, setSettings] = useState({
    hvac_labor_rate: 95,
    plumbing_labor_rate: 85,
    margin_percent: 0.65,
    minimum_price_floor: "",
    ai_tone: "Friendly & Reassuring",
    ai_length: "Standard",
    enable_permit_fee: false,
    enable_subcontractor_cost: false,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API_URL}/settings`, {
          headers: getAuthHeader(),
        });
        setSettings({
          ...response.data,
          minimum_price_floor: response.data.minimum_price_floor || "",
        });
      } catch (error) {
        console.error("Failed to fetch settings:", error);
        toast.error("Failed to load settings");
      } finally {
        setIsLoading(false);
      }
    };
    fetchSettings();
  }, [getAuthHeader]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await axios.put(
        `${API_URL}/settings`,
        {
          ...settings,
          minimum_price_floor: settings.minimum_price_floor ? parseFloat(settings.minimum_price_floor) : null,
        },
        { headers: getAuthHeader() }
      );
      toast.success("Settings saved!");
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save settings");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-primary font-oswald text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass-header sticky top-0 z-40 px-4 py-3">
        <div className="max-w-2xl mx-auto flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/")}
            className="text-slate-600"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-oswald font-bold text-[#003366] text-xl tracking-tight">ADMIN SETTINGS</h1>
            <p className="text-xs text-slate-500">Configure pricing and AI behavior</p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6 pb-24">
        {/* Pricing Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="font-oswald text-[#003366] flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              PRICING CONFIGURATION
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Plumbing Labor Rate */}
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                <Droplets className="w-4 h-4 text-blue-500" />
                Plumbing Labor Rate ($/hour)
              </Label>
              <div className="relative">
                <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  type="number"
                  min="0"
                  step="1"
                  value={settings.plumbing_labor_rate}
                  onChange={(e) => setSettings({ ...settings, plumbing_labor_rate: parseFloat(e.target.value) || 0 })}
                  className="h-14 pl-12 text-lg rounded-xl border-2"
                  data-testid="plumbing-labor-rate-input"
                />
              </div>
            </div>

            {/* HVAC Labor Rate */}
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                <Flame className="w-4 h-4 text-orange-500" />
                HVAC Labor Rate ($/hour)
              </Label>
              <div className="relative">
                <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  type="number"
                  min="0"
                  step="1"
                  value={settings.hvac_labor_rate}
                  onChange={(e) => setSettings({ ...settings, hvac_labor_rate: parseFloat(e.target.value) || 0 })}
                  className="h-14 pl-12 text-lg rounded-xl border-2"
                  data-testid="hvac-labor-rate-input"
                />
              </div>
            </div>

            {/* Default Profit Predictor */}
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Default Profit Predictor
              </Label>
              <div className="relative">
                <Percent className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  type="number"
                  min="0.52"
                  max="0.62"
                  step="0.01"
                  value={settings.margin_percent}
                  onChange={(e) => setSettings({ ...settings, margin_percent: parseFloat(e.target.value) || 0 })}
                  className="h-14 pl-12 text-lg rounded-xl border-2"
                  data-testid="margin-percent-input"
                />
              </div>
              <p className="text-xs text-slate-500">
                Techs can adjust between 52%-62% using the Profit Predictor slider. Default: {Math.round(settings.margin_percent * 100)}%
              </p>
            </div>

            {/* Minimum Price Floor */}
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Minimum Price Floor (optional)
              </Label>
              <div className="relative">
                <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  type="number"
                  min="0"
                  step="1"
                  placeholder="No minimum"
                  value={settings.minimum_price_floor}
                  onChange={(e) => setSettings({ ...settings, minimum_price_floor: e.target.value })}
                  className="h-14 pl-12 text-lg rounded-xl border-2"
                  data-testid="min-price-input"
                />
              </div>
            </div>

            {/* Optional Fields */}
            <div className="space-y-4 pt-4 border-t border-slate-200">
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500">Optional Cost Fields</h4>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-700">Permit / Disposal Fee</p>
                  <p className="text-sm text-slate-500">Allow technicians to add permit costs</p>
                </div>
                <Switch
                  checked={settings.enable_permit_fee}
                  onCheckedChange={(checked) => setSettings({ ...settings, enable_permit_fee: checked })}
                  data-testid="enable-permit-switch"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-700">Subcontractor Cost</p>
                  <p className="text-sm text-slate-500">Allow technicians to add subcontractor costs</p>
                </div>
                <Switch
                  checked={settings.enable_subcontractor_cost}
                  onCheckedChange={(checked) => setSettings({ ...settings, enable_subcontractor_cost: checked })}
                  data-testid="enable-subcontractor-switch"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* AI Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="font-oswald text-[#003366] flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              AI SUMMARY SETTINGS
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* AI Tone */}
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Summary Tone
              </Label>
              <Select
                value={settings.ai_tone}
                onValueChange={(value) => setSettings({ ...settings, ai_tone: value })}
              >
                <SelectTrigger className="h-14 text-lg rounded-xl border-2" data-testid="ai-tone-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Professional">Professional</SelectItem>
                  <SelectItem value="Friendly & Reassuring">Friendly & Reassuring</SelectItem>
                  <SelectItem value="Direct & Technical">Direct & Technical</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* AI Length */}
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Summary Length
              </Label>
              <Select
                value={settings.ai_length}
                onValueChange={(value) => setSettings({ ...settings, ai_length: value })}
              >
                <SelectTrigger className="h-14 text-lg rounded-xl border-2" data-testid="ai-length-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Short">Short (2-3 sentences)</SelectItem>
                  <SelectItem value="Standard">Standard (3-5 sentences)</SelectItem>
                  <SelectItem value="Detailed">Detailed (5-7 sentences)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <Button
          onClick={handleSave}
          disabled={isSaving}
          className="w-full h-14 text-lg font-bold uppercase tracking-wide bg-[#003366] hover:bg-[#002244] text-white rounded-xl shadow-lg active:scale-95 transition-transform"
          data-testid="save-settings-btn"
        >
          {isSaving ? (
            <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full spinner" />
          ) : (
            <>
              <Save className="w-5 h-5 mr-2" />
              Save Settings
            </>
          )}
        </Button>
      </main>
    </div>
  );
}
